import React, { useState, useEffect } from 'react';
import { Share2, Download, Copy, RefreshCw, Sparkles, CheckCircle2 } from 'lucide-react';

interface PositionInfo {
  id: string;
  name: string;
  department: string;
  location: string;
}

interface AuthResult {
  employee_name: string;
  referral_code: string;
  career_token: string;
  positions: PositionInfo[];
}

const captionPool = [
  '我在这里等你，一起做点不一样的事。内推码见海报，机会不等人！',
  '好公司值得安利，好机会值得分享。我的内推码在海报上，快来投！',
  '听说你想换工作？我这里有个好去处，内推码已经准备好了，私聊也行！',
  '与其海投简历，不如找我内推。内推码就在海报上，一步到位！',
  '与其等机会，不如创造机会。内推码我备好了，下一个加入的就是你！',
  '工作能不能开心很重要。我现在就很开心，所以把机会分享给你。内推码见海报！',
  '身边的好机会，不能只有我知道。内推码已就位，等你来！',
  '好的团队值得被更多人知道。内推码在海报上，一起成为同事吧！',
  '终于遇到一家让我心甘情愿安利的公司。内推码已备好，速来！',
  '同事问我为什么总是笑，因为我在等你的简历呀！内推码见海报～',
  '我在这家公司，遇见了更好的自己。现在，轮到你了。内推码在海报上！',
  '一个人走得快，一群人走得远。内推码已准备，等你一起走！',
  '推荐你来，不是因为我有任务，是因为我真的觉得你适合这里。内推码见海报！',
  '每次有人用我的内推码入职，我都比自己入职还开心。下一个会是你吗？',
  '有些机会，只对「认识人」的人开放。现在你也认识了！内推码在海报上～',
  '面试路上少走弯路的方法：找我内推。码在海报上，简历走起！',
  '我知道你一直想找更好的平台，这次别犹豫了。内推码已就位，冲！',
  '分享一个我亲身验证过的好机会。内推码在海报上，放心投递！',
  '一个内推码，可能就是你职业生涯的转折点。别犹豫，来吧！',
  '我已经帮你省掉了海投的烦恼，内推码就在海报上，剩下的交给你了！',
];

const pickCaptions = () => [...captionPool].sort(() => Math.random() - 0.5).slice(0, 6);

export default function ReferralPosterPage() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [authError, setAuthError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [authData, setAuthData] = useState<AuthResult | null>(null);
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [captions, setCaptions] = useState<string[]>(() => pickCaptions());
  const [copiedCaption, setCopiedCaption] = useState<number | null>(null);
  const [posterSaving, setPosterSaving] = useState(false);
  const [posterMessage, setPosterMessage] = useState('');

  // Feishu state
  const [feishuEnabled, setFeishuEnabled] = useState(false);
  const [feishuAppId, setFeishuAppId] = useState('');
  const [feishuLoading, setFeishuLoading] = useState(false);

  const [copiedCode, setCopiedCode] = useState(false);

  // On mount: check feishu config & handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle Feishu OAuth callback
    const feishuCode = params.get('code');
    const state = params.get('state');
    if (feishuCode && state === 'feishu_referrer') {
      window.history.replaceState({}, '', window.location.pathname);
      setFeishuLoading(true);
      const redirectUri = window.location.origin + '/referral-poster';
      fetch('/api/auth/feishu-referrer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: feishuCode, redirect_uri: redirectUri }),
      })
        .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
        .then(({ ok, data }) => {
          if (!ok) throw new Error(data.message);
          setAuthData(data);
        })
        .catch(err => setAuthError(err.message || '飞书登录失败'))
        .finally(() => setFeishuLoading(false));
      return;
    }

    // Check if Feishu OAuth is enabled
    fetch('/api/auth/feishu-config')
      .then(r => r.json())
      .then(cfg => {
        if (cfg.enabled && cfg.app_id) {
          setFeishuEnabled(true);
          setFeishuAppId(cfg.app_id);
        }
      })
      .catch(() => {});
  }, []);

  const handleFeishuLogin = () => {
    const redirectUri = encodeURIComponent(window.location.origin + '/referral-poster');
    const authUrl = `https://accounts.feishu.cn/open-apis/authen/v1/authorize?app_id=${feishuAppId}&redirect_uri=${redirectUri}&state=feishu_referrer`;
    window.location.href = authUrl;
  };

  const handleAuth = async () => {
    if (!name.trim() || !phone.trim()) return;
    setVerifying(true);
    setAuthError('');
    try {
      const res = await fetch('/api/referral/poster-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAuthData(data);
    } catch (err: any) {
      setAuthError(err.message || '验证失败');
    } finally {
      setVerifying(false);
    }
  };

  const copyCaption = async (text: string, index: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedCaption(index);
    setTimeout(() => setCopiedCaption(null), 1600);
  };

  const copyReferralCode = async () => {
    if (!authData) return;
    await navigator.clipboard.writeText(authData.referral_code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 1600);
  };

  const savePoster = async () => {
    setPosterSaving(true);
    setPosterMessage('');
    try {
      const response = await fetch('/referral-template-poster.png');
      const blob = await response.blob();
      const file = new File([blob], '内推官方海报.png', { type: blob.type || 'image/png' });
      const nav = navigator as Navigator & {
        canShare?: (data: { files?: File[] }) => boolean;
        share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
      };

      if (nav.canShare?.({ files: [file] }) && nav.share) {
        await nav.share({ files: [file], title: '内推官方海报', text: '保存或分享内推官方海报' });
        setPosterMessage('已打开系统保存/分享面板');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '内推官方海报.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setPosterMessage('已开始下载；如未保存，请长按上方海报选择保存图片');
    } catch (err) {
      window.open('/referral-template-poster.png', '_blank');
      setPosterMessage('已打开海报原图，请长按图片保存');
    } finally {
      setPosterSaving(false);
    }
  };

  // ─── Feishu loading ────────────────────────────────────────
  if (feishuLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-white border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-sm">正在通过飞书登录...</p>
        </div>
      </div>
    );
  }

  // ─── Auth page ──────────────────────────────────────────────
  if (!authData) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <div className="fixed top-0 left-0 right-0 z-20 h-11 bg-black/80 backdrop-blur-xl flex items-center justify-center">
          <div className="flex items-center gap-2 text-white text-sm font-semibold">
            <img src="/referral-logo.png" alt="留形科技" className="h-6 w-auto" />
            留形科技
          </div>
        </div>

        <section className="min-h-screen px-5 pt-28 pb-16 flex flex-col items-center justify-center text-center">
          <img src="/referral-logo.png" alt="留形科技" className="h-20 w-auto mb-7" />
          <h1 className="text-5xl md:text-6xl font-semibold tracking-tight text-gray-950">内推计划</h1>
          <p className="text-2xl md:text-3xl text-gray-700 mt-5 max-w-2xl leading-snug">飞书登录后获取你的专属 5 位内推码</p>
          <div className="flex items-center gap-3 mt-9 flex-wrap justify-center">
            <a href="#generate" className="px-6 py-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors">获取内推码</a>
            <a href="#poster" className="px-6 py-3 rounded-full border border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors">查看官方海报</a>
          </div>
        </section>

        <section id="generate" className="bg-[#272729] px-5 py-20 text-center">
          <h2 className="text-4xl font-semibold text-white">获取你的专属内推码</h2>
          <p className="text-xl text-gray-300 mt-3 mb-10">登录后自动创建或找回你的 5 位纯数字内推码</p>

          <div className="bg-[#2a2a2c] rounded-3xl p-6 md:p-10 max-w-xl mx-auto shadow-2xl">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Sparkles size={32} className="text-blue-400" />
            </div>
            <div className="space-y-4">
              {feishuEnabled && (
                <button onClick={handleFeishuLogin}
                  className="w-full py-3 flex items-center justify-center gap-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-full transition-colors">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 7.5L10.5 3L15 10.5L7.5 15L3 7.5Z" fill="#00D6B9"/>
                    <path d="M10.5 3L21 7.5L15 10.5L10.5 3Z" fill="#3370FF"/>
                    <path d="M15 10.5L21 7.5L18 21L7.5 15L15 10.5Z" fill="#3370FF"/>
                    <path d="M7.5 15L18 21L3 18L7.5 15Z" fill="#00D6B9"/>
                  </svg>
                  飞书一键登录获取内推码
                </button>
              )}

              {feishuEnabled && !showPhoneForm && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-white/10" />
                  <button onClick={() => setShowPhoneForm(true)} className="text-xs text-gray-400 hover:text-white whitespace-nowrap">
                    或用手机号生成
                  </button>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
              )}

              {(!feishuEnabled || showPhoneForm) && (
                <>
                  <div className="text-left">
                    <label className="text-xs font-medium text-gray-300 block mb-1">姓名</label>
                    <input value={name} onChange={e => setName(e.target.value)}
                      placeholder="请输入您的姓名"
                      className="w-full px-5 py-3 bg-white/10 border border-white/10 rounded-full text-sm text-white placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                  </div>
                  <div className="text-left">
                    <label className="text-xs font-medium text-gray-300 block mb-1">手机号</label>
                    <input value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="请输入您的手机号" maxLength={11} type="tel"
                      className="w-full px-5 py-3 bg-white/10 border border-white/10 rounded-full text-sm text-white placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                  </div>
                  <button onClick={handleAuth} disabled={verifying || !name.trim() || !phone.trim()}
                    className="w-full py-3 bg-blue-600 text-white rounded-full font-medium text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    {verifying ? '正在生成...' : '生成专属内推码'}
                  </button>
                </>
              )}

              {authError && <p className="text-red-500 text-xs text-center">{authError}</p>}
            </div>
          </div>

          <p className="text-center text-gray-500 text-xs mt-6">首次使用自动注册为内推官，重复登录会自动找回原内推码</p>
        </section>

        <section id="poster" className="bg-gray-100 px-5 py-20 text-center">
          <h2 className="text-4xl font-semibold text-gray-950">官方内推海报</h2>
          <p className="text-xl text-gray-500 mt-3 mb-10">统一使用官方海报，不再按单个岗位生成海报</p>
          <div className="max-w-sm mx-auto bg-white rounded-3xl p-4 shadow-xl">
            <img src="/referral-template-poster.png" alt="内推海报" className="rounded-2xl w-full" />
          </div>
          <button onClick={savePoster} disabled={posterSaving} className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-black text-white hover:bg-gray-800 disabled:opacity-60">
            <Download size={18} />{posterSaving ? '处理中...' : '保存官方海报'}
          </button>
          <p className="text-xs text-gray-500 mt-3">{posterMessage || '手机端如未自动保存，可长按海报图片保存到相册'}</p>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-7">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Share2 size={20} />
          {authData.employee_name} 的内推工具包
        </h1>
        <p className="text-indigo-200 text-xs mt-1">复制内推码，下载官方海报，搭配文案即可转发</p>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-green-50 text-green-600 flex items-center justify-center mb-3">
            <CheckCircle2 size={26} />
          </div>
          <p className="text-xs text-gray-500 mb-2">你的专属 5 位内推码</p>
          <div className="text-5xl font-black tracking-[0.18em] text-gray-950 font-mono">{authData.referral_code}</div>
          <button
            onClick={copyReferralCode}
            className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
          >
            <Copy size={16} />{copiedCode ? '已复制' : '复制内推码'}
          </button>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
            <Download size={16} className="text-indigo-500" />
            官方内推海报
          </h2>
          <div className="rounded-2xl overflow-hidden bg-gray-100">
            <img src="/referral-template-poster.png" alt="官方内推海报" className="w-full block" />
          </div>
          <button
            onClick={savePoster}
            disabled={posterSaving}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60"
          >
            <Download size={16} />{posterSaving ? '处理中...' : '保存/分享官方海报'}
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">{posterMessage || '飞书内打开时如未保存，请长按上方海报选择保存图片'}</p>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Copy size={15} className="text-indigo-500" />
              宣传文案
            </h3>
            <button
              onClick={() => setCaptions(pickCaptions())}
              className="text-xs text-indigo-600 flex items-center gap-1 hover:text-indigo-700"
            >
              <RefreshCw size={13} />换一批
            </button>
          </div>
          <div className="space-y-2">
            {captions.map((caption, index) => (
              <div key={`${caption}-${index}`} className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                <p className="text-xs text-gray-600 leading-relaxed">{caption}</p>
                <button
                  onClick={() => copyCaption(caption, index)}
                  className="mt-2 text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
                >
                  {copiedCaption === index ? '已复制' : '复制文案'}
                </button>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
